import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, Trash2, Star, Package, Image as ImageIcon, Plus } from 'lucide-react';
import api from '../services/api';

interface ProductVariant {
  _id?: string;
  id?: string;
  sku: string;
  attributes: Array<{ name: string; value: string }>;
  price: number;
  stock?: number;
  quantityAvailable?: number;
  images?: string[];
}

interface Product {
  id?: string;
  _id?: string;
  name: string;
  description: string;
  brand: string;
  price?: number;
  originalPrice?: number;
  productType: 'simple' | 'variable';
  category: {
    id?: string;
    _id?: string;
    name: string;
  };
  owner: {
    id?: string;
    _id?: string;
    name: string;
    type: string;
    official: boolean;
  };
  isActive: boolean;
  isFlash: boolean;
  isRecommended: boolean;
  rating: number;
  noOfReviews: number;
  quantityAvailable?: number;
  totalStock?: number;
  images?: string[];
  variants?: ProductVariant[]; // Product variants with SKUs
  createdAt: string;
}

interface ProductsResponse {
  products: Product[];
  total: number;
  totalPages: number;
  currentPage: number;
}


const Products: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState<{id: string; name: string; parent?: string | null}[]>([]);

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '12',
        ...(searchTerm && { search: searchTerm }),
        ...(filterActive && { isActive: filterActive }),
        ...(filterCategory && { category: filterCategory }),
      });

      const response = await api.get(`/admin/products?${params}`);
      const data: ProductsResponse = response.data.data;
      
      // Normalize products: convert _id to id if needed
      const normalizedProducts = data.products.map((product: any) => ({
        ...product,
        id: product.id || product._id?.toString() || product._id,
        category: {
          ...product.category,
          id: product.category?.id || product.category?._id?.toString() || product.category?._id,
        },
        owner: {
          ...product.owner,
          id: product.owner?.id || product.owner?._id?.toString() || product.owner?._id,
        },
      }));
      
      setProducts(normalizedProducts);
      setTotal(data.total);
      setTotalPages(data.totalPages);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, filterActive, filterCategory]);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/admin/categories');
      setCategories(response.data.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [currentPage, searchTerm, filterActive, filterCategory, fetchProducts]);

  const handleToggleStatus = async (productId: string | undefined, isActive: boolean) => {
    if (!productId) {
      console.error('Product ID is undefined');
      return;
    }
    try {
      await api.put(`/admin/products/${productId}/status`, { isActive: !isActive });
      fetchProducts();
    } catch (error) {
      console.error('Error updating product status:', error);
    }
  };

  const handleToggleFeatured = async (productId: string | undefined, type: 'recommended' | 'flash', currentValue: boolean) => {
    if (!productId) {
      console.error('Product ID is undefined');
      return;
    }
    try {
      const payload = type === 'recommended' 
        ? { isRecommended: !currentValue }
        : { isFlash: !currentValue };
      
      await api.put(`/admin/products/${productId}/featured`, payload);
      fetchProducts();
    } catch (error) {
      console.error('Error updating product featured status:', error);
    }
  };

  const handleDeleteProduct = async (productId: string | undefined, productName: string) => {
    if (!productId) {
      console.error('Product ID is undefined');
      return;
    }
    if (window.confirm(t('messages.confirmDelete', { ns: 'products', name: productName }))) {
      try {
        await api.delete(`/admin/products/${productId}`);
        fetchProducts();
      } catch (error) {
        console.error('Error deleting product:', error);
      }
    }
  };


  const formatPrice = (price?: number) => {
    return price ? `$${price.toFixed(2)}` : 'N/A';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('title', { ns: 'products' })}</h1>
          <p className="text-gray-600 mt-2">{t('subtitle', { ns: 'products' })}</p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="text-sm text-gray-500">
            {t('table.totalResults', { ns: 'common' })}: {total} {t('title', { ns: 'products' }).toLowerCase()}
          </div>
          <button
            onClick={() => navigate('/products/add')}
            className="btn-primary flex items-center space-x-2"
          >
            <Plus size={20} />
            <span>{t('addProduct', { ns: 'products' })}</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder={t('search', { ns: 'products' })}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-field pl-10"
            />
          </div>

          {/* Status Filter */}
          <select
            value={filterActive}
            onChange={(e) => setFilterActive(e.target.value)}
            className="input-field"
          >
            <option value="">{t('filters.allStatus', { ns: 'products' })}</option>
            <option value="true">{t('filters.active', { ns: 'products' })}</option>
            <option value="false">{t('filters.inactive', { ns: 'products' })}</option>
          </select>

          {/* Category Filter */}
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="input-field"
          >
            <option value="">{t('filters.allCategories', { ns: 'products' })}</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>

          {/* Clear Filters */}
          <button
            onClick={() => {
              setSearchTerm('');
              setFilterActive('');
              setFilterCategory('');
              setCurrentPage(1);
            }}
            className="btn-outline"
          >
            {t('buttons.clearFilters', { ns: 'common' })}
          </button>
        </div>
      </div>

      {/* Products Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {loading ? (
          <div className="col-span-full flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          </div>
        ) : products.length === 0 ? (
          <div className="col-span-full text-center py-12 text-gray-500">
            {searchTerm || filterActive || filterCategory ? t('messages.noProductsMatchingFilters', { ns: 'products' }) : t('messages.noProductsFound', { ns: 'products' })}
          </div>
        ) : (
          products.map((product, index) => (
            <div key={product.id || `product-${index}`} className="card hover:shadow-card-hover transition-shadow">
              {/* Product Image */}
              <div className="relative h-48 bg-gray-100 rounded-t-lg">
                {product.images && product.images.length > 0 ? (
                  <img
                    src={product.images[0]}
                    alt={product.name}
                    className="w-full h-full object-cover rounded-t-lg"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      target.nextElementSibling?.classList.remove('hidden');
                    }}
                  />
                ) : null}
                <div className={`absolute inset-0 flex items-center justify-center bg-gray-100 rounded-t-lg ${product.images && product.images.length > 0 ? 'hidden' : ''}`}>
                  <ImageIcon size={48} className="text-gray-400" />
                </div>

                {/* Status Badges */}
                <div className="absolute top-2 left-2 flex flex-col space-y-1">
                  <span className={`badge-${product.isActive ? 'green' : 'red'} text-xs`}>
                    {product.isActive ? t('status.active', { ns: 'common' }) : t('status.inactive', { ns: 'common' })}
                  </span>
                  {product.isRecommended && (
                    <span className="badge-yellow text-xs">{t('badges.recommended', { ns: 'products' })}</span>
                  )}
                  {product.isFlash && (
                    <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-medium">{t('badges.flash', { ns: 'products' })}</span>
                  )}
                </div>
              </div>

              {/* Product Info */}
              <div className="p-4">
                <div className="mb-2">
                  <h3 className="font-semibold text-gray-900 text-sm line-clamp-2 mb-1">
                    {product.name}
                  </h3>
                  <p className="text-xs text-gray-500 mb-2">{product.brand}</p>
                </div>

                {/* Price and Rating */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    {product.price && (
                      <span className="font-bold text-primary-600">
                        {formatPrice(product.price)}
                      </span>
                    )}
                    {product.originalPrice && product.originalPrice > (product.price || 0) && (
                      <span className="text-xs text-gray-500 line-through">
                        {formatPrice(product.originalPrice)}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center space-x-1">
                    <Star size={12} className="text-yellow-400 fill-current" />
                    <span className="text-xs text-gray-600">
                      {product.rating.toFixed(1)} ({product.noOfReviews})
                    </span>
                  </div>
                </div>

                {/* Category and Owner */}
                <div className="text-xs text-gray-500 mb-3">
                  <div>{t('fields.category', { ns: 'products' })}: {product.category.name}</div>
                  <div>{t('fields.seller', { ns: 'products' })}: {product.owner.name}</div>
                  {(() => {
                    const variantStock = product.variants?.reduce(
                      (sum, v) => sum + (v.quantityAvailable ?? v.stock ?? 0),
                      0
                    );
                    const displayStock =
                      product.totalStock ??
                      (variantStock && variantStock > 0 ? variantStock : undefined) ??
                      product.quantityAvailable;
                    return displayStock !== undefined ? (
                      <div>
                        {t('fields.stock', { ns: 'products' })}: {displayStock}
                      </div>
                    ) : null;
                  })()}
                  {product.variants && product.variants.length > 0 && (
                    <div className="mt-2">
                      <div className="font-semibold text-gray-700 mb-1">SKUs:</div>
                      {product.variants.slice(0, 3).map((variant) => (
                        <div key={variant._id || variant.id} className="text-xs">
                          <span className="font-mono text-blue-600">{variant.sku}</span>
                          {variant.attributes && variant.attributes.length > 0 && (
                            <span className="text-gray-500 ml-1">
                              ({variant.attributes.map(a => `${a.name}: ${a.value}`).join(', ')})
                            </span>
                          )}
                        </div>
                      ))}
                      {product.variants.length > 3 && (
                        <div className="text-xs text-gray-400">+{product.variants.length - 3} more</div>
                      )}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => handleToggleStatus(product.id, product.isActive)}
                      className={`btn-${product.isActive ? 'outline' : 'primary'} text-xs py-1 px-2`}
                    >
                      {product.isActive ? t('buttons.deactivate', { ns: 'common' }) : t('buttons.activate', { ns: 'common' })}
                    </button>
                  </div>

                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => handleToggleFeatured(product.id, 'recommended', product.isRecommended)}
                      className={`p-1 rounded ${product.isRecommended ? 'text-yellow-600' : 'text-gray-400'} hover:text-yellow-600`}
                      title={t('actions.toggleRecommended', { ns: 'products' })}
                    >
                      <Star size={16} className={product.isRecommended ? 'fill-current' : ''} />
                    </button>
                    <button
                      onClick={() => handleToggleFeatured(product.id, 'flash', product.isFlash)}
                      className={`p-1 rounded ${product.isFlash ? 'text-red-600' : 'text-gray-400'} hover:text-red-600`}
                      title={t('actions.toggleFlashSale', { ns: 'products' })}
                    >
                      <Package size={16} />
                    </button>
                    <button
                      onClick={() => handleDeleteProduct(product.id, product.name)}
                      className="p-1 text-gray-400 hover:text-red-600"
                      title={t('actions.deleteProduct', { ns: 'products' })}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                {/* Date */}
                <div className="text-xs text-gray-400 mt-2">
                  {t('messages.added', { ns: 'products' })} {formatDate(product.createdAt)}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="card p-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-500">
              {t('table.showing', { ns: 'common' })} {((currentPage - 1) * 12) + 1} {t('table.to', { ns: 'common' })} {Math.min(currentPage * 12, total)} {t('table.of', { ns: 'common' })} {total} {t('table.results', { ns: 'common' })}
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="btn-outline text-sm py-1 px-2 disabled:opacity-50"
              >
                {t('table.previous', { ns: 'common' })}
              </button>
              <span className="text-sm text-gray-700">
                {t('table.page', { ns: 'common' })} {currentPage} {t('table.of', { ns: 'common' })} {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="btn-outline text-sm py-1 px-2 disabled:opacity-50"
              >
                {t('table.next', { ns: 'common' })}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Products;